import * as React from 'react';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Button } from 'components';
import { TOrderCheckoutItem } from 'api/orders/orders.types';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { TOrderFormValues } from '../../OrderForm.types';

export const OrderFormLineItemsSection: React.FC = () => {
  const localize = useLocalize();
  const modals = useModals();
  const { control, getValues } = useFormContext<TOrderFormValues>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'checkoutItems',
  });

  const checkoutLines = useWatch({
    control,
    name: 'checkoutItems',
  }) as TOrderCheckoutItem[] | undefined;

  const openAddModal = React.useCallback(() => {
    modals.openModal(MODALS.ADMIN_ORDER_LINE_ADD, {
      onConfirm: (line: TOrderCheckoutItem) => append(line),
    });
  }, [append, modals]);

  const openEditModal = React.useCallback(
    (lineIndex: number) => {
      const line = getValues(`checkoutItems.${lineIndex}`);
      modals.openModal(MODALS.ADMIN_ORDER_LINE_EDIT, {
        initialLine: line,
        onConfirm: (updated: TOrderCheckoutItem) => update(lineIndex, updated),
      });
    },
    [getValues, modals, update]
  );

  return (
    <section className="admin-orders-detail__section">
      <div className="admin-order-form__line-items-header">
        <h3 className="h6 admin-orders-detail__section-title">
          {localize('admin.orders.detail.section.items')}
        </h3>
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={openAddModal}
        >
          {localize('admin.orders.form.line.addproduct')}
        </Button>
      </div>

      {!fields.length ? (
        <p className="body2 admin-order-form__line-empty">
          {localize('admin.orders.form.line.empty')}
        </p>
      ) : null}

      {fields.map((field, lineIndex) => {
        const line = checkoutLines?.[lineIndex];
        const firstStock = line?.stock?.[0];

        return (
          <div key={field.id} className="admin-form__custom-field">
            <div className="admin-form__custom-field-header">
              <div className="admin-order-form__line-summary">
                {line?.productImage ? (
                  <img
                    className="admin-order-form__line-thumb"
                    src={line.productImage}
                    alt=""
                  />
                ) : null}
                <div>
                  <p className="body2 font-semibold">
                    {line?.productName
                      ? (localize(line.productName) as string)
                      : (localize('admin.orders.form.line.noname') as string)}
                  </p>
                  {firstStock ? (
                    <p className="caption admin-order-form__line-stock-raw">
                      {firstStock.name} — {firstStock.variation}
                    </p>
                  ) : null}
                  <p className="caption">
                    {localize('admin.orders.detail.label.unitprice')}:{' '}
                    {line?.price != null ? line.price.toFixed(2) : '—'} ·{' '}
                    {localize('admin.orders.detail.label.quantity')}:{' '}
                    {line?.quantity ?? '—'}
                  </p>
                </div>
              </div>
              <div className="admin-order-form__line-actions">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => openEditModal(lineIndex)}
                >
                  {localize('admin.orders.edit')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Delete />}
                  disabled={fields.length <= 1}
                  onClick={() => remove(lineIndex)}
                >
                  {localize('admin.orders.form.line.remove')}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};
